import { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";

function AutocompleteCollege({
    claimedEmail,
    ref = null,
    setLocation = (newloc) => {},
    detectedSubdomain = "",
    setAllValidColleges = () => {},
    hidden = false,
}) {
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [savedData, setSavedData] = useState([]); // wuad rows
    const [themesData, setThemesData] = useState({});
    const [filteredSuggestions, setFilteredSuggestions] = useState([]);
    const [lockInput, setLockInput] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const [userTouched, setUserTouched] = useState(false);

    const extractDomainFromEmail = (email) => {
        const parts = (email || "").split("@");
        if (parts.length === 2) {
            return (parts[1] || "").trim().toLowerCase();
        }
        return "";
    };

    const looksLikeDomain = (val) => {
        const v = (val || "").trim().toLowerCase();
        if (!v || v.includes(" ")) return false;
        if (!v.includes(".")) return false;
        return /[a-z0-9-]+\.[a-z]{2,}$/.test(v);
    };

    const normalizeDomainInput = (val) => {
        return (val || "").trim().toLowerCase();
    };

    /**
     * Fetch wuad.json + school_themes.json ONCE
     */
    useEffect(() => {
        const controller = new AbortController();

        (async () => {
            try {
                setLoading(true);

                const [wuadRes, themesRes] = await Promise.all([
                    fetch("/static/wuad.json", { signal: controller.signal }),
                    fetch("/api/school_themes", { signal: controller.signal }),
                ]);

                const wuadData = wuadRes.ok ? await wuadRes.json() : [];
                const themes = themesRes.ok ? await themesRes.json() : {};

                setSavedData(wuadData);
                setThemesData(themes);

                const collegeNames = wuadData.map((college) => college.name);
                setAllValidColleges(collegeNames);
            } catch (err) {
                if (err.name !== "AbortError") {
                    Sentry.captureException(err);
                    console.error("AutocompleteCollege fetch error:", err);
                }
            } finally {
                setLoading(false);
            }
        })();

        return () => controller.abort();
    }, []);

    /**
     * Lock input if subdomain is detected
     */
    useEffect(() => {
        if (!detectedSubdomain) return;

        const theme = themesData?.[detectedSubdomain];
        if (!theme?.full_name) return;

        setInput(theme.full_name);
        setLocation(theme.full_name);
        setLockInput(true);
        setFilteredSuggestions([]);
    }, [detectedSubdomain, themesData, setLocation]);

    /**
     * Auto-fill from claimed email domain (only if user hasn't touched input)
     */
    useEffect(() => {
        if (lockInput) return;
        if (savedData.length === 0) return;
        if (userTouched) return;
        if ((input || "").trim().length > 0) return;

        const domain = extractDomainFromEmail(claimedEmail);
        if (!domain) return;

        const matches = savedData.filter((row) =>
            (row.domains || []).includes(domain)
        );

        if (matches.length >= 1) {
            const collegeName = matches[0].name;
            setInput(collegeName);
            setLocation(collegeName);
        }
    }, [savedData, claimedEmail, lockInput, userTouched, input, setLocation]);

    /**
     * Close dropdown on outside click
     */
    useEffect(() => {
        const handleClick = (event) => {
            if (event.target.id !== "college") {
                setFilteredSuggestions([]);
            }
        };

        document.addEventListener("click", handleClick);
        return () => document.removeEventListener("click", handleClick);
    }, []);

    const buildSuggestions = (value) => {
        const v = (value || "").trim();
        const lowerVal = v.toLowerCase();
        const footer = ["None", "School not listed"];

        if (!lowerVal) {
            return footer;
        }

        if (looksLikeDomain(lowerVal)) {
            const dom = normalizeDomainInput(lowerVal);

            const domainHits = savedData
                .filter((row) => (row.domains || []).includes(dom))
                .map((row) => row.name);

            const nameHits = savedData
                .map((row) => row.name)
                .filter((name) => name.toLowerCase().includes(lowerVal));

            const merged = [];
            const pushUnique = (arr) => {
                for (const item of arr) {
                    if (!merged.includes(item)) {
                        merged.push(item);
                    }
                }
            };

            pushUnique(domainHits);
            pushUnique(nameHits);

            return [...merged.slice(0, 25), "None", "School not listed"];
        }

        const nameHits = savedData
            .map((row) => row.name)
            .filter((name) => name.toLowerCase().includes(lowerVal));

        return [...nameHits.slice(0, 25), ...footer];
    };

    if (hidden) {
        return null;
    }

    return (
        <div
            style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                position: "relative",
            }}
            onMouseEnter={() => lockInput && setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
        >
            <div style={{ flex: 1, position: "relative" }}>
                <input
                    value={loading ? "..." : input}
                    placeholder="Enter domain (ex: mit.edu) or search by name"
                    required
                    type="text"
                    id="college"
                    name="college"
                    aria-label="Enter your college"
                    ref={ref}
                    readOnly={lockInput}
                    onChange={(event) => {
                        if (lockInput) return;
                        setUserTouched(true);
                        const val = event.target.value;
                        setInput(val);
                        setFilteredSuggestions(buildSuggestions(val));
                        setLocation(val);
                    }}
                    onFocus={() => {
                        if (lockInput) return;
                        setUserTouched(true);
                        setFilteredSuggestions(buildSuggestions(input));
                    }}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            const first = filteredSuggestions[0] || input;
                            setInput(first);
                            setLocation(first);
                            setFilteredSuggestions([]);
                        }
                    }}
                    style={{ width: "95%" }}
                />

                {lockInput && showTooltip && (
                    <div
                        style={{
                            position: "absolute",
                            top: "-25px",
                            left: 0,
                            backgroundColor: "#333",
                            color: "#fff",
                            padding: "5px 10px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            whiteSpace: "normal",
                            maxWidth: "200px",
                            zIndex: 200,
                        }}
                    >
                        You cannot edit this field. Leave the school portal to
                        change your college.
                        <br />
                        <a
                            href="https://rezify.ai/register"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                color: "#aad7ff",
                                textDecoration: "underline",
                            }}
                        >
                            Rezify
                        </a>
                    </div>
                )}

                {!lockInput && filteredSuggestions.length > 0 && (
                    <ul
                        style={{
                            listStyle: "none",
                            padding: 0,
                            margin: 0,
                            backgroundColor: "white",
                            border: "1px solid var(--primary-color)",
                            maxHeight: "200px",
                            overflowY: "auto",
                            position: "absolute",
                            width: "100%",
                            zIndex: 100,
                        }}
                    >
                        {filteredSuggestions.map((item, i) => (
                            <li
                                key={`${item}-${i}`}
                                className="college-suggestion-item"
                                onClick={() => {
                                    setUserTouched(true);
                                    setInput(item);
                                    setLocation(item);
                                    setFilteredSuggestions([]);
                                }}
                            >
                                {item
                                    .split(new RegExp(`(${input})`, "gi"))
                                    .map((part, index) =>
                                        part.toLowerCase() ===
                                        input.toLowerCase() ? (
                                            <span
                                                key={index}
                                                style={{
                                                    backgroundColor:
                                                        "var(--primary-color)",
                                                    color: "white",
                                                }}
                                            >
                                                {part}
                                            </span>
                                        ) : (
                                            <span key={index}>{part}</span>
                                        )
                                    )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

export { AutocompleteCollege };
